import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import FieldError from '../FormComponents/FieldError';

/**
 * THE form-field chrome.
 *
 * Before this, every control drew its own label ("block text-sm text-gray-700
 * dark:text-gray-300", but sometimes "font-medium", sometimes "mb-1", sometimes
 * "mb-2"), its own required asterisk, its own description paragraph, and its own
 * error row. Four focus-ring systems coexisted and one of them - `ring-grayscale-500`
 * - pointed at a palette that does not exist, so the resting ring rendered nothing.
 *
 * FieldShell owns all of it. Controls bring only their own input element and get
 * the label/description/error rows for free, in the same rhythm everywhere.
 *
 * `fieldSurface()` is the other half: the single field-state matrix that every
 * control's focusable surface runs through, so an Input, a Dropdown trigger and a
 * DatePicker trigger standing side by side are indistinguishable at rest and behave
 * identically on hover, focus, error and disable.
 */

export interface FieldSurfaceOptions {
  /** Renders the at-rest danger border + ring (visible without focus). */
  error?: boolean;
  disabled?: boolean;
  /** Strips the fill and border entirely - a value that reads as text, not a field. */
  readOnly?: boolean;
  /** Tag-style controls grow, so they get `min-h-9` instead of `h-9`. */
  multiline?: boolean;
  /**
   * Opts out of the height class entirely. Textareas carry their own height from
   * the caller (`h`, `rows`); emitting `min-h-9` alongside it would leave two
   * equal-specificity `min-height` rules whose winner depends on Tailwind's
   * output order rather than on this argument list.
   */
  height?: false;
}

/**
 * The field-state matrix, in one place.
 *
 * default   bg-surface-sunken/70 + border-hairline - NO champagne at rest
 * hover     bg-surface-sunken
 * focus     bg-surface + border-champagne/60 + ring-2 champagne/20
 * error     border-danger-500 + resting ring-1 danger/30
 * err+focus ring-2 danger/40
 * disabled  bg-surface-sunken/40 + border-hairline-soft + not-allowed
 * readonly  transparent fill and border
 *
 * Champagne appears on exactly one field state - focus - and at a weight low
 * enough to read as a highlight rather than a glow. See FORM_SPEC.md.
 */
export function fieldSurface({
  error,
  disabled,
  readOnly,
  multiline,
  height,
}: FieldSurfaceOptions = {}): string {
  return clsx(
    'w-full rounded-md border text-sm text-fg-strong',
    'placeholder:text-fg-muted/60',
    'transition-colors duration-200 focus:outline-none',
    height !== false && (multiline ? 'min-h-9' : 'h-9'),

    readOnly
      ? 'bg-transparent border-transparent'
      : disabled
        ? 'bg-surface-sunken/40 border-hairline-soft text-fg-muted cursor-not-allowed'
        : error
          ? [
              'bg-surface-sunken/70 border-danger-500 ring-1 ring-danger-500/30',
              'hover:bg-surface-sunken',
              'focus:bg-surface focus:border-danger-500',
              'focus-visible:ring-2 focus-visible:ring-danger-500/40',
            ]
          : [
              'bg-surface-sunken/70 border-hairline',
              'hover:bg-surface-sunken',
              'focus:bg-surface focus:border-champagne/60',
              'focus-visible:ring-2 focus-visible:ring-champagne/20',
            ]
  );
}

/**
 * The focus treatment for surfaces that are not text fields - a dropdown trigger,
 * a dropzone, a segmented control - where `fieldSurface()` fill rules don't apply
 * but the ring must still match.
 */
export const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/20 focus:border-champagne/60';

/**
 * The same ring for controls that own no border to tint - an icon button, a
 * native checkbox, a clear affordance. Same weight, so every focusable thing in
 * a form lights up identically.
 */
export const FOCUS_RING_ONLY =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/20';

/**
 * The selected/on treatment for chips, segments and option tiles: a soft tint,
 * not a saturated fill. Solid champagne is reserved for small active elements
 * (a toggle track, a check glyph) where the area is a few pixels, not a surface.
 */
export const SELECTED_TINT = 'bg-champagne/10 border-champagne/40 text-fg-strong';

/* ---- the canonical label / value / help / error type scale ---- */

/** Field label. The one label style in the app. */
export const FIELD_LABEL_CLASS = 'block text-sm font-medium text-fg-body';
/** The required marker, always immediately after the label text. */
export const FIELD_REQUIRED_CLASS = 'ms-0.5 text-fg-muted';
/** Standing explanation under the label, and any help line under the control. */
export const FIELD_HELP_CLASS = 'text-xs text-fg-muted';
/** Value text for controls that render their value as text rather than in an input. */
export const FIELD_VALUE_CLASS = 'text-sm text-fg-strong';
/** Placeholder text, in a control that draws its own. */
export const FIELD_PLACEHOLDER_CLASS = 'text-sm text-fg-muted/60';
/** Label tone when the field is in error. */
export const FIELD_LABEL_ERROR_CLASS = 'text-danger-500';
/** The one vertical step between a label, its control and its help/error row. */
export const FIELD_GAP = 'mb-1.5';

export interface FieldShellProps {
  /** Wired to the label's `htmlFor`. */
  id?: string;
  label?: string;
  required?: boolean;
  /** Muted line under the label - the standing explanation of the field. */
  description?: string;
  /** Legacy italic hint some forms still pass. Rendered above the description. */
  italicText?: string;
  /** Shown in the error row; also what callers use to drive `fieldSurface({error})`. */
  error?: string;
  /** Muted line under the control. Suppressed while an error is showing. */
  helpText?: string;
  /** Rendered inline after the label - the info "i" button lives here. */
  labelSuffix?: React.ReactNode;
  /** Applied to the outer wrapper. */
  className?: string;
  /** Skips `t()` on label/description/helpText for callers passing pre-translated text. */
  translate?: boolean;
  children: React.ReactNode;
}

const FieldShell: React.FC<FieldShellProps> = ({
  id,
  label,
  required,
  description,
  italicText,
  error,
  helpText,
  labelSuffix,
  className,
  translate = true,
  children,
}) => {
  const { t } = useTranslation();
  const tr = (value: string) => (translate ? t(value) : value);

  return (
    <div className={clsx('flex-1 min-w-0', className)}>
      {(label || labelSuffix) && (
        <div className={clsx(FIELD_GAP, 'flex items-center gap-1')}>
          {label && (
            <label htmlFor={id} className={FIELD_LABEL_CLASS}>
              {tr(label)}
              {required && <span className={FIELD_REQUIRED_CLASS}>*</span>}
            </label>
          )}
          {labelSuffix}
        </div>
      )}

      {italicText && <p className={clsx(FIELD_GAP, FIELD_HELP_CLASS, 'italic')}>{tr(italicText)}</p>}
      {description && <p className={clsx(FIELD_GAP, FIELD_HELP_CLASS)}>{tr(description)}</p>}

      {children}

      <FieldError error={error} />

      {helpText && !error && <p className={clsx('mt-1.5', FIELD_HELP_CLASS)}>{tr(helpText)}</p>}
    </div>
  );
};

export default FieldShell;
